import { useState, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea } from "@/components/voice-field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Loader2, CheckCircle, ArrowRight, ArrowLeft } from "lucide-react";
import { ClarikMark } from "@/components/clarik-mark";

// ─── Palette ────────────────────────────────────────────────────────────────

const PAPER       = "#F5F1E8";
const PAPER_LIGHT = "#FBF8EF";
const PAPER_DIM   = "#EFE9D8";
const INK         = "#1C1C1A";
const INK_SOFT    = "#3A3A33";
const FOG         = "#DDD8C9";
const STONE       = "#C9C2AC";
const GRAPHITE    = "#6B6B5E";
const GREEN       = "#1A4D3A";
const GREEN_SOFT  = "#DCE8DF";
const TEAL        = "#5DCAA5";
const GOLD        = "#C8A24A";

// ─── Constants ──────────────────────────────────────────────────────────────

const FALLBACK_BUSINESS_TYPES = [
  "Speaker", "Emcee / Host", "Comedian", "Musician / Band", "Tribute Act",
  "DJ", "Magician", "Variety Act", "Dancer / Group", "Talent Agency",
  "Talent Manager", "Booking Agent", "Production / Entertainment Company", "Other",
];

const HEARD_ABOUT = [
  "Google Search",
  "Social Media (Instagram / TikTok / Facebook)",
  "Word of Mouth / Referral",
  "Industry Event or Conference",
  "Podcast or YouTube",
  "Email / Newsletter",
  "Other",
];

const TEAM_SIZES = [
  "Just me", "2–5 people", "6–15 people", "16–50 people", "50+ people",
];

const ANNUAL_REVENUES = [
  "Under $25k", "$25k – $75k", "$75k – $150k",
  "$150k – $500k", "$500k – $1M", "$1M+", "Prefer not to say",
];

const TECH_OPTIONS = [
  { key: "mass_emailer",       label: "Mass Email / Marketing" },
  { key: "crm",                label: "CRM (Client Management)" },
  { key: "invoicing",          label: "Automatic Invoicing" },
  { key: "lead_gen",           label: "Lead Generation" },
  { key: "quickbooks",         label: "QuickBooks / Accounting" },
  { key: "event_manager",      label: "Event Manager" },
  { key: "contract_signing",   label: "E-Signature / Contracts" },
  { key: "scheduling",         label: "Scheduling / Calendar" },
  { key: "social_media",       label: "Social Media Management" },
  { key: "payment_processing", label: "Payment Processing" },
  { key: "spreadsheets",       label: "Spreadsheets" },
  { key: "nothing",            label: "Nothing — pen & paper" },
];

const FEATURES = [
  {
    num: "01",
    name: "Find work",
    items: [
      "AI lead generation & prospecting",
      "Google Places + Apollo enrichment",
      "screenshot-to-lead AI import",
      "automatic lead scoring",
      "an AI first-responder that engages inbound leads before a rep touches them",
    ],
  },
  {
    num: "02",
    name: "Book it",
    items: [
      "Multi-view master schedule",
      "AI tour planner with route optimization",
      "double-booking guards",
      "public booking pages your clients use without a login",
      "multi-talent, multi-slot events",
    ],
  },
  {
    num: "03",
    name: "Get paid",
    items: [
      "Automated invoicing straight from booked events",
      "late-fee scheduler",
      "direct pay through Stripe",
      "deposit collection & automated payouts",
      "white-label payment portals",
      "cash-flow analytics",
    ],
  },
  {
    num: "04",
    name: "Communicate",
    items: [
      "Full Gmail integration with AI reply drafting",
      "unified email + SMS inbox",
      "mass email",
      "multi-channel drip campaigns",
      "Twilio SMS",
      "trigger-based automations",
    ],
  },
  {
    num: "05",
    name: "Let AI work",
    items: [
      "A role-aware voice assistant with read/write access to your CRM",
      "AI that qualifies leads, drafts replies & enriches contacts",
      "AI image generation for social",
      "an AI outbound caller in your own cloned voice",
    ],
  },
];

const STEP_LABELS = ["About you", "Your business", "Today"];

// ─── Shared input style ──────────────────────────────────────────────────────

const inputCls =
  "border-[#DDD8C9] text-[#1C1C1A] placeholder:text-[#8A8A7A] focus-visible:ring-[#1A4D3A]/20 focus-visible:border-[#1A4D3A] text-[15px]";

// ─── Page ───────────────────────────────────────────────────────────────────

export default function BetaApplyPage() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState(1);
  const formRef = useRef<HTMLElement>(null);

  const { data: platformBizTypes = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/platform/business-types"],
  });
  const businessTypes = platformBizTypes.length > 0
    ? platformBizTypes.map((t) => t.name)
    : FALLBACK_BUSINESS_TYPES;

  const [name, setName]               = useState("");
  const [email, setEmail]             = useState("");
  const [phone, setPhone]             = useState("");
  const [company, setCompany]         = useState("");
  const [businessType, setBusinessType] = useState("");
  const [teamSize, setTeamSize]       = useState("");
  const [annualRevenue, setAnnualRevenue] = useState("");
  const [annualShows, setAnnualShows] = useState("");
  const [goals, setGoals]             = useState("");
  const [heardAbout, setHeardAbout]   = useState("");
  const [currentTech, setCurrentTech] = useState<string[]>([]);

  const submitMutation = useMutation({
    mutationFn: (data: object) => apiRequest("POST", "/api/platform/beta/submit", data),
    onSuccess: () => setSubmitted(true),
    onError: (err: any) => {
      const msg = err?.message || "Something went wrong. Please try again.";
      toast({ title: "Submission failed", description: msg, variant: "destructive" });
    },
  });

  function toggleTech(key: string) {
    setCurrentTech((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }

  function scrollToForm() {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function goNext() {
    if (step === 1) {
      if (!name || !email) {
        toast({ title: "Please fill in your name and email", variant: "destructive" });
        return;
      }
    }
    if (step === 2) {
      if (!businessType || !annualShows) {
        toast({ title: "Please fill in the required fields", variant: "destructive" });
        return;
      }
    }
    setStep((s) => Math.min(s + 1, 3));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function goBack() {
    setStep((s) => Math.max(s - 1, 1));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function handleSubmit() {
    if (!name || !email || !businessType) {
      toast({ title: "Please fill in required fields", variant: "destructive" });
      return;
    }
    submitMutation.mutate({
      funnelSlug: "beta",
      name,
      email,
      phone: phone || undefined,
      company: company || undefined,
      businessType,
      heardAbout: heardAbout || undefined,
      teamSize: teamSize || undefined,
      annualRevenue: annualRevenue || undefined,
      annualShows: annualShows ? parseInt(annualShows, 10) : undefined,
      goals: goals || undefined,
      currentTech,
    });
  }

  if (submitted) {
    return <SuccessScreen name={name} />;
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: PAPER, color: INK, fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif" }}>

      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <nav
        className="flex items-center justify-between px-6 md:px-12 py-6"
        style={{ borderBottom: `0.5px solid ${FOG}` }}
      >
        <ClarikMark className="text-xl" dotColor={GREEN} />
        <span
          className="text-xs px-3.5 py-1.5 rounded-full"
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            letterSpacing: "0.08em",
            border: `0.5px solid ${GREEN}`,
            color: GREEN,
          }}
          data-testid="badge-limited-beta"
        >
          LIMITED BETA
        </span>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="px-6 md:px-12 pt-16 md:pt-20 pb-14 text-center max-w-[1100px] mx-auto w-full">
        <p
          className="text-xs uppercase mb-7"
          style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", letterSpacing: "0.08em", color: GRAPHITE }}
          data-testid="text-hero-eyebrow"
        >
          Early access <span style={{ color: GREEN }}>/</span> application
        </p>

        <h1
          className="font-medium leading-none mb-5"
          style={{ fontSize: "clamp(48px, 8vw, 84px)", letterSpacing: "-0.045em", color: INK }}
          data-testid="text-hero-headline"
        >
          Crush your tech stack.<br />
          <span style={{ color: GRAPHITE + "CC" }}>One login replaces six.</span>
        </h1>

        <p
          className="text-lg leading-relaxed mb-10 max-w-[620px] mx-auto"
          style={{ color: INK_SOFT }}
          data-testid="text-hero-lead"
        >
          Clarik is the all-in-one platform for entertainment booking — lead gen, client management, scheduling, invoicing, payments, email, and a team of{" "}
          <strong style={{ fontWeight: 500, color: INK }}>AI assistants that never sleep</strong>.
          Stop duct-taping tools together. Start booking more and billing faster.
        </p>

        {/* Offer banner */}
        <div
          className="relative rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 max-w-[880px] mx-auto text-left"
          style={{ background: INK, color: PAPER }}
          data-testid="banner-offer"
        >
          <span
            className="absolute -top-2.5 left-8 px-2.5 py-0.5 rounded-full text-[10px] font-medium tracking-wide uppercase"
            style={{ background: GOLD, color: INK, letterSpacing: "0.08em" }}
            data-testid="badge-phase-1"
          >
            Phase 1
          </span>
          <div className="flex flex-col sm:flex-row gap-8 sm:gap-10">
            <div>
              <div
                className="font-medium leading-none mb-1.5"
                style={{ fontSize: "clamp(22px, 3vw, 32px)", letterSpacing: "-0.03em" }}
              >
                4 months <span style={{ color: TEAL }}>free</span>
              </div>
              <div className="text-[13px] leading-snug" style={{ color: STONE }}>
                Full access, every feature.<br />No card to apply.
              </div>
            </div>
            <div>
              <div
                className="font-medium leading-none mb-1.5"
                style={{ fontSize: "clamp(22px, 3vw, 32px)", letterSpacing: "-0.03em" }}
              >
                $50<span className="text-base" style={{ color: STONE }}>/mo</span>
              </div>
              <div className="text-[13px] leading-snug" style={{ color: STONE }}>
                Then you get our $129 plan,<br />locked in at $50 — for life.
              </div>
            </div>
            <div>
              <div
                className="font-medium leading-none mb-1.5"
                style={{ fontSize: "clamp(22px, 3vw, 32px)", letterSpacing: "-0.03em" }}
              >
                Founding
              </div>
              <div className="text-[13px] leading-snug" style={{ color: STONE }}>
                Limited spots. Onboarded<br />by hand, in small batches.
              </div>
            </div>
          </div>
          <button
            onClick={scrollToForm}
            className="shrink-0 px-6 py-3.5 rounded-full text-[15px] font-medium transition-opacity hover:opacity-90"
            style={{ background: PAPER, color: INK, whiteSpace: "nowrap" }}
            data-testid="button-claim-spot"
          >
            Claim your spot ↓
          </button>
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────────────────── */}
      <section
        className="px-6 md:px-12 py-16 md:py-20"
        style={{ background: PAPER_DIM, borderTop: `0.5px solid ${FOG}`, borderBottom: `0.5px solid ${FOG}` }}
      >
        <div className="max-w-[1100px] mx-auto">
          <h2
            className="font-medium mb-12 max-w-[700px]"
            style={{ fontSize: "clamp(28px, 4vw, 40px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
            data-testid="text-features-heading"
          >
            You'd need six subscriptions to do what Clarik does in one.
          </h2>

          {FEATURES.map((f, i) => (
            <div
              key={f.num}
              className="py-6 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3 md:gap-8"
              style={{
                borderTop: `0.5px solid ${FOG}`,
                borderBottom: i === FEATURES.length - 1 ? `0.5px solid ${FOG}` : undefined,
              }}
              data-testid={`feature-bucket-${f.num}`}
            >
              <div className="flex items-baseline gap-2.5">
                <span
                  className="text-xs"
                  style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: GREEN }}
                >
                  {f.num}
                </span>
                <span className="text-xl font-medium" style={{ letterSpacing: "-0.02em" }}>
                  {f.name}
                </span>
              </div>
              <div className="text-[15px] leading-[1.7]" style={{ color: INK_SOFT }}>
                {f.items.map((item, j) => (
                  <span key={j}>
                    {item}
                    {j < f.items.length - 1 && (
                      <span className="mx-1" style={{ color: STONE }}>·</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          ))}

          <p
            className="text-center mt-10 text-2xl"
            style={{ fontFamily: "'Fraunces', Georgia, serif", fontStyle: "italic", letterSpacing: "-0.015em", color: INK }}
            data-testid="text-short-list"
          >
            And that's the short list.
          </p>
        </div>
      </section>

      {/* ── Form Section ─────────────────────────────────────────────────── */}
      <section
        ref={formRef}
        className="px-6 md:px-12 py-16 md:py-20 max-w-[720px] mx-auto w-full"
        data-testid="section-apply-form"
      >
        <div className="text-center mb-10">
          <h2
            className="font-medium mb-3"
            style={{ fontSize: "clamp(32px, 5vw, 44px)", letterSpacing: "-0.035em" }}
          >
            Apply for early access
          </h2>
          <p style={{ color: GRAPHITE }}>Three quick steps. Takes about two minutes.</p>
        </div>

        {/* Progress indicator */}
        <div className="flex items-start mb-10 max-w-[520px] mx-auto" data-testid="progress-indicator">
          {[1, 2, 3].map((s) => {
            const isActive = s === step;
            const isDone   = s < step;
            const isLine   = s < 3;
            return (
              <div key={s} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center gap-1.5 relative">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-medium shrink-0"
                    style={{
                      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                      background: isActive || isDone ? GREEN : PAPER_DIM,
                      color: isActive || isDone ? PAPER : GRAPHITE,
                      border: isActive || isDone ? "none" : `0.5px solid ${FOG}`,
                    }}
                    data-testid={`progress-dot-${s}`}
                  >
                    {isDone ? "✓" : s}
                  </div>
                  <span
                    className="text-[12px] whitespace-nowrap"
                    style={{ color: isActive ? INK : GRAPHITE }}
                  >
                    {STEP_LABELS[s - 1]}
                  </span>
                </div>
                {isLine && (
                  <div
                    className="flex-1 h-px mx-2 mb-5"
                    style={{ background: isDone ? GREEN : FOG }}
                    data-testid={`progress-line-${s}`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Form card */}
        <div
          className="rounded-2xl p-8 md:p-10"
          style={{ background: PAPER_LIGHT, border: `0.5px solid ${FOG}` }}
        >
          {/* Step 1: About you */}
          {step === 1 && (
            <div data-testid="form-step-1">
              <p
                className="text-xs uppercase mb-1"
                style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", letterSpacing: "0.08em", color: GREEN }}
              >
                Step 1 of 3
              </p>
              <h3 className="text-2xl font-medium mb-7" style={{ letterSpacing: "-0.02em" }}>
                About you
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <Label className="text-[14px] font-medium mb-2 block" style={{ color: INK }}>
                    Full Name <span style={{ color: GREEN }}>*</span>
                  </Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Smith"
                    className={inputCls}
                    style={{ background: PAPER }}
                    data-testid="input-apply-name"
                  />
                </div>
                <div>
                  <Label className="text-[14px] font-medium mb-2 block" style={{ color: INK }}>
                    Email <span style={{ color: GREEN }}>*</span>
                  </Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="booking@comedymike.com"
                    className={inputCls}
                    style={{ background: PAPER }}
                    data-testid="input-apply-email"
                  />
                </div>
                <div>
                  <Label className="text-[14px] font-medium mb-2 block" style={{ color: INK }}>Phone</Label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className={inputCls}
                    style={{ background: PAPER }}
                    data-testid="input-apply-phone"
                  />
                </div>
                <div>
                  <Label className="text-[14px] font-medium mb-2 block" style={{ color: INK }}>Company / Act Name</Label>
                  <Input
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Mike Martin Illusions"
                    className={inputCls}
                    style={{ background: PAPER }}
                    data-testid="input-apply-company"
                  />
                </div>
              </div>

              <FormNav onNext={goNext} />
            </div>
          )}

          {/* Step 2: Your business */}
          {step === 2 && (
            <div data-testid="form-step-2">
              <p
                className="text-xs uppercase mb-1"
                style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", letterSpacing: "0.08em", color: GREEN }}
              >
                Step 2 of 3
              </p>
              <h3 className="text-2xl font-medium mb-7" style={{ letterSpacing: "-0.02em" }}>
                Your business
              </h3>

              <div className="mb-5">
                <Label className="text-[14px] font-medium mb-2 block" style={{ color: INK }}>
                  Business Type <span style={{ color: GREEN }}>*</span>
                </Label>
                <Select value={businessType} onValueChange={setBusinessType}>
                  <SelectTrigger className={cn(inputCls, "w-full")} style={{ background: PAPER }} data-testid="select-apply-business-type">
                    <SelectValue placeholder="Select your type" />
                  </SelectTrigger>
                  <SelectContent>
                    {businessTypes.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <Label className="text-[14px] font-medium mb-2 block" style={{ color: INK }}>Team Size</Label>
                  <Select value={teamSize} onValueChange={setTeamSize}>
                    <SelectTrigger className={cn(inputCls, "w-full")} style={{ background: PAPER }} data-testid="select-apply-team-size">
                      <SelectValue placeholder="How big is your team?" />
                    </SelectTrigger>
                    <SelectContent>
                      {TEAM_SIZES.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[14px] font-medium mb-2 block" style={{ color: INK }}>Annual Revenue</Label>
                  <Select value={annualRevenue} onValueChange={setAnnualRevenue}>
                    <SelectTrigger className={cn(inputCls, "w-full")} style={{ background: PAPER }} data-testid="select-apply-revenue">
                      <SelectValue placeholder="Approximate annual revenue" />
                    </SelectTrigger>
                    <SelectContent>
                      {ANNUAL_REVENUES.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mb-5">
                <Label className="text-[14px] font-medium mb-2 block" style={{ color: INK }}>
                  Approx. Bookings / Year <span style={{ color: GREEN }}>*</span>
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={annualShows}
                  onChange={(e) => setAnnualShows(e.target.value)}
                  placeholder="e.g. 120"
                  className={inputCls}
                  style={{ background: PAPER }}
                  data-testid="input-apply-annual-shows"
                />
              </div>

              <FormNav onBack={goBack} onNext={goNext} />
            </div>
          )}

          {/* Step 3: Where you are today */}
          {step === 3 && (
            <div data-testid="form-step-3">
              <p
                className="text-xs uppercase mb-1"
                style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", letterSpacing: "0.08em", color: GREEN }}
              >
                Step 3 of 3
              </p>
              <h3 className="text-2xl font-medium mb-7" style={{ letterSpacing: "-0.02em" }}>
                Where you are today
              </h3>

              <div className="mb-6">
                <Label className="text-[14px] font-medium mb-1 block" style={{ color: INK }}>Current Tech Stack</Label>
                <p className="text-[13px] mb-3" style={{ color: GRAPHITE }}>
                  What tools are you using today to run your business? Select all that apply.
                </p>
                <div className="flex flex-wrap gap-2">
                  {TECH_OPTIONS.map((opt) => {
                    const selected = currentTech.includes(opt.key);
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => toggleTech(opt.key)}
                        className="px-3.5 py-2 rounded-full text-[13px] transition-all"
                        style={
                          selected
                            ? { background: GREEN_SOFT, border: `0.5px solid ${GREEN}`, color: GREEN, fontWeight: 500 }
                            : { background: PAPER, border: `0.5px solid ${FOG}`, color: INK_SOFT }
                        }
                        data-testid={`checkbox-tech-${opt.key}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mb-5">
                <Label className="text-[14px] font-medium mb-1 block" style={{ color: INK }}>Your Goals</Label>
                <p className="text-[13px] mb-2" style={{ color: GRAPHITE }}>
                  What are your biggest goals for your business in the next 12 months?
                </p>
                <VoiceToTextTextarea
                  value={goals}
                  onChange={(e) => setGoals(e.target.value)}
                  placeholder="Book more corporate events, stop losing leads in my inbox, finally get paid on time…"
                  rows={4}
                  className={cn(inputCls, "resize-none")}
                  style={{ background: PAPER }}
                  data-testid="textarea-apply-goals"
                />
              </div>

              <div className="mb-5">
                <Label className="text-[14px] font-medium mb-2 block" style={{ color: INK }}>How did you hear about Clarik?</Label>
                <Select value={heardAbout} onValueChange={setHeardAbout}>
                  <SelectTrigger className={cn(inputCls, "w-full")} style={{ background: PAPER }} data-testid="select-apply-heard">
                    <SelectValue placeholder="Select one" />
                  </SelectTrigger>
                  <SelectContent>
                    {HEARD_ABOUT.map((h) => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <FormNav
                onBack={goBack}
                isSubmit
                isLoading={submitMutation.isPending}
                onSubmit={handleSubmit}
              />
            </div>
          )}
        </div>

        <p className="text-center text-[13px] mt-8" style={{ color: GRAPHITE }}>
          No credit card to apply. We're approving applicants in small batches.
        </p>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer
        className="px-6 md:px-12 py-8 flex items-center justify-between text-[13px] mt-auto"
        style={{ borderTop: `0.5px solid ${FOG}`, color: GRAPHITE }}
      >
        <div>
          clarik{" "}
          <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>/</span>
          {" "}2026
        </div>
        <div className="flex items-center gap-6">
          <a href="/help" className="hover:opacity-70 transition-opacity" data-testid="link-footer-help">Help</a>
          <a href="/security" className="hover:opacity-70 transition-opacity" data-testid="link-footer-security">Security</a>
          <a href="/terms" className="hover:opacity-70 transition-opacity" data-testid="link-footer-terms">Terms</a>
          <a href="/privacy" className="hover:opacity-70 transition-opacity" data-testid="link-footer-privacy">Privacy</a>
        </div>
      </footer>
    </div>
  );
}

// ─── FormNav ────────────────────────────────────────────────────────────────

function FormNav({
  onBack,
  onNext,
  isSubmit,
  isLoading,
  onSubmit,
}: {
  onBack?: () => void;
  onNext?: () => void;
  isSubmit?: boolean;
  isLoading?: boolean;
  onSubmit?: () => void;
}) {
  const FOG_   = "#DDD8C9";
  const GREEN_ = "#1A4D3A";
  const PAPER_ = "#F5F1E8";
  const GRAPH_ = "#6B6B5E";

  return (
    <div
      className="flex items-center justify-between mt-8 pt-6"
      style={{ borderTop: `0.5px solid ${FOG_}` }}
    >
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-[14px] transition-opacity hover:opacity-70"
          style={{ color: GRAPH_, background: "none", border: "none", cursor: "pointer" }}
          data-testid="button-apply-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      ) : (
        <span />
      )}
      {isSubmit ? (
        <button
          type="button"
          onClick={onSubmit}
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[14px] font-medium transition-opacity hover:opacity-90 disabled:opacity-60"
          style={{ background: GREEN_, color: PAPER_, border: "none", cursor: "pointer" }}
          data-testid="button-apply-submit"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting…
            </>
          ) : (
            <>
              Submit application
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      ) : (
        <button
          type="button"
          onClick={onNext}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[14px] font-medium transition-opacity hover:opacity-90"
          style={{ background: GREEN_, color: PAPER_, border: "none", cursor: "pointer" }}
          data-testid="button-apply-next"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// ─── SuccessScreen ───────────────────────────────────────────────────────────

function SuccessScreen({ name }: { name: string }) {
  const firstName = name.split(" ")[0];
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 text-center"
      style={{ backgroundColor: PAPER, color: INK }}
    >
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
        style={{ backgroundColor: `${GREEN}1A`, border: `1px solid ${GREEN}40` }}
      >
        <CheckCircle className="w-10 h-10" style={{ color: GREEN }} />
      </div>
      <h1 className="text-3xl font-medium tracking-tight mb-3" style={{ color: INK }}>
        You're on the list, {firstName}!
      </h1>
      <p className="text-lg max-w-md leading-relaxed mb-8" style={{ color: GRAPHITE }}>
        You'll hear from us soon! We're currently waiting on verifications but we should be ready to launch by as early as next week!
      </p>
      <div className="flex flex-col gap-2 text-sm" style={{ color: GRAPHITE }}>
        <p>In the meantime, follow us for updates:</p>
        <div className="flex items-center gap-3 justify-center mt-1">
          <a href="#" className="transition-colors hover:opacity-80" style={{ color: GREEN }} data-testid="link-success-instagram">Instagram</a>
          <span style={{ color: FOG }}>·</span>
          <a href="#" className="transition-colors hover:opacity-80" style={{ color: GREEN }} data-testid="link-success-tiktok">TikTok</a>
          <span style={{ color: FOG }}>·</span>
          <a href="#" className="transition-colors hover:opacity-80" style={{ color: GREEN }} data-testid="link-success-linkedin">LinkedIn</a>
        </div>
      </div>
    </div>
  );
}
